import React from "react";
import { PageLayout } from "@/components/layout/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Loader2, Users, Building, FileText, Settings, GitBranch, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useBuildingCount } from "@/hooks/buildings/useBuildingCount";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { PageTitle } from "@/components/ui/page-title";

export default function AdminDashboard() {
  const { t } = useLanguage();

  const { data: userCount, isLoading: loadingUsers } = useQuery({
    queryKey: ["admin-user-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("user_profiles")
        .select("*", { count: "exact", head: true });

      if (error) throw error;
      return count || 0;
    },
  });

  const { data: buildingCount, isLoading: loadingBuildings } = useBuildingCount();

  const { data: documentCount, isLoading: loadingDocuments } = useQuery({
    queryKey: ["admin-document-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("documents")
        .select("*", { count: "exact", head: true });

      if (error) throw error;
      return count || 0;
    },
  });

  return (
    <PageLayout headerButtons={<AdminHeader />}>
      <PageTitle className="mb-6">{t.common.adminDashboard}</PageTitle>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t.common.users}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingUsers ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <div className="text-xl font-bold">{userCount}</div>
                <p className="text-xs text-muted-foreground">{t.common.registeredUsers}</p>
                <Button variant="outline" size="sm" className="mt-4" asChild>
                  <Link to="/admin/users">{t.common.manageUsers}</Link>
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t.common.buildings}</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingBuildings ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <div className="text-xl font-bold">{buildingCount}</div>
                <p className="text-xs text-muted-foreground">{t.common.totalBuildings}</p>
                <Button variant="outline" size="sm" className="mt-4" asChild>
                  <Link to="/buildings">{t.common.viewBuildings}</Link>
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t.common.documents}</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loadingDocuments ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <div className="text-xl font-bold">{documentCount}</div>
                <p className="text-xs text-muted-foreground">{t.common.uploadedDocuments}</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>{t.common.adminSettings}</CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="default" asChild>
              <Link to="/admin/settings">
                <Settings className="mr-2 h-4 w-4" />
                {t.common.systemSettings}
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Data Management</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              Manage building data attributes and AI extraction groups
            </p>
            <Button variant="default" asChild>
              <Link to="/admin/data-management">
                <Settings className="mr-2 h-4 w-4" />
                Configure Data
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.common.pipelines}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">{t.common.pipelinesDescription}</p>
            <Button variant="default" asChild>
              <Link to="/admin/data-management">
                <GitBranch className="mr-2 h-4 w-4" />
                View Pipelines
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Roles &amp; Permissions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              Edit which permissions each role grants, and create new roles.
            </p>
            <Button variant="default" asChild>
              <Link to="/admin/roles">
                <ShieldCheck className="mr-2 h-4 w-4" />
                Manage Roles
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
